<template>
  <div class="modal-overlay" @click.self="closeModal">
    <div class="modal-container">
      <div class="modal-header">
        <h4 class="modal-title">{{ modalHeading }}</h4>
        <button class="modal-close" @click="closeModal" aria-label="Close">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        
        <form @submit.prevent="handleSubmit">
          <div class="form-group">
            <label class="form-label">Organization</label>
            <select 
              v-model="selectedOrganization" 
              @change="onOrganizationChange"
              class="form-select"
            >
              <option value="" disabled>Select an organization</option>
              <option 
                v-for="org in dropdownList" 
                :key="org.organization_id"
                :value="org.organization_id"
              >
                {{ org.organization_name }}
              </option>
            </select>
          </div>

          <div class="form-group" v-if="availableLocations.length > 0">
            <label class="form-label">Location</label>
            <select 
              v-model="selectedLocationId" 
              class="form-select"
              required
            >
              <option value="" disabled>Select a location</option>
              <option 
                v-for="location in availableLocations" 
                :key="location.location_id"
                :value="location.location_id"
              >
                {{ location.location_name }}
              </option>
            </select>
          </div>
          
          <div class="modal-actions">
            <material-button 
              type="button"
              color="outline-secondary"
              @click="closeModal"
            >
              Cancel
            </material-button>
            
            <material-button 
              type="submit"
              variant="gradient" 
              color="success"
              :disabled="!selectedLocationId"
            >
              Continue
            </material-button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import MaterialButton from '../common/MaterialButton.vue';

export default {
  name: 'LocationModal',
  components: {
    MaterialButton
  },
  props: {
    modalHeading: {
      type: String,
      default: 'Select Location'
    },
    dropdownList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedOrganization: '',
      selectedLocationId: '',
      availableLocations: []
    }
  },
  mounted() {
    // Auto-select if only one organization
    if (this.dropdownList && this.dropdownList.length === 1) {
      this.selectedOrganization = this.dropdownList[0].organization_id;
      this.onOrganizationChange();
    }
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    onOrganizationChange() {
      const org = this.dropdownList.find(
        o => o.organization_id === this.selectedOrganization
      );
      
      if (org && org.locations) {
        this.availableLocations = org.locations;
        
        // Auto-select if only one location
        if (this.availableLocations.length === 1) {
          this.selectedLocationId = this.availableLocations[0].location_id;
        } else {
          this.selectedLocationId = '';
        }
      } else {
        this.availableLocations = [];
        this.selectedLocationId = '';
      }
    },
    handleSubmit() {
      if (this.selectedLocationId) {
        console.log("LocationModal: Submitting location:", this.selectedLocationId);
        this.$emit('selectedValue', this.selectedLocationId);
      }
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 20px;
}

.modal-container {
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 28px;
  border-bottom: 1px solid #e9ecef;
  background: linear-gradient(to bottom, #ffffff, #fafbfc);
}

.modal-title {
  margin: 0;
  font-size: 1.375rem;
  font-weight: 600;
  color: #344767;
  letter-spacing: -0.01em;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: #8392ab;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.modal-close:hover {
  background-color: #f0f2f5;
  color: #344767;
}

.modal-close:active {
  transform: scale(0.95);
}

.modal-body {
  padding: 32px 28px;
  overflow-y: auto;
}

.modal-description {
  margin: 0 0 28px 0;
  color: #67748e;
  font-size: 0.9375rem;
  line-height: 1.5;
}

.form-group {
  margin-bottom: 24px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #344767;
  letter-spacing: 0.01em;
}

.form-select {
  width: 100%;
  padding: 12px 16px;
  font-size: 0.9375rem;
  color: #495057;
  background-color: #fff;
  border: 1px solid #d2d6da;
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23344767' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  padding-right: 40px;
}

.form-select:hover {
  border-color: #b8bfc6;
}

.form-select:focus {
  outline: none;
  border-color: #82d616;
  box-shadow: 0 0 0 3px rgba(130, 214, 22, 0.1);
}

.form-select:disabled {
  background-color: #f8f9fa;
  cursor: not-allowed;
  opacity: 0.6;
}

.form-select option {
  padding: 8px;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e9ecef;
}

.modal-actions button {
  min-width: 110px;
  padding: 10px 24px;
  font-weight: 600;
  font-size: 0.9375rem;
}

.btn-cancel {
  background-color: transparent !important;
  border: 1px solid #d2d6da !important;
  color: #67748e !important;
}

/* .btn-cancel:hover {
  background-color: #f8f9fa !important;
  border-color: #adb5bd !important;
  color: #344767 !important;
} */

/* Responsive adjustments */
@media (max-width: 576px) {
  .modal-container {
    max-width: 100%;
    border-radius: 12px;
  }
  
  .modal-header,
  .modal-body {
    padding: 20px;
  }
  
  .modal-title {
    font-size: 1.25rem;
  }
  
  .modal-actions {
    flex-direction: column-reverse;
    gap: 10px;
  }
  
  .modal-actions button {
    width: 100%;
    min-width: auto;
  }
}
</style>